/**
 * Admin — Comunicazioni CRUD.
 * Route: /admin/announcements
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { AlertTriangle, Bell, Info, Paperclip, Plus, X, Upload } from "lucide-react";

const PRIORITIES = [
  { value: "URGENT", label: "Urgente" },
  { value: "IMPORTANT", label: "Importante" },
  { value: "NORMAL", label: "Normale" },
];
const PRIORITY_TONE = { URGENT: "danger", IMPORTANT: "warning", NORMAL: "info" };
const TARGET_TYPES = [
  { value: "ALL", label: "Tutti i clienti" },
  { value: "CATEGORY", label: "Categoria clienti" },
  { value: "REGION", label: "Regione" },
  { value: "SELECTED", label: "Clienti selezionati" },
];

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; }

export function AdminAnnouncementsPage() {
  const { apiGet, apiPost, apiPatch, apiDelete, token } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // null | {} | row
  const [attachTarget, setAttachTarget] = useState(null);
  // Mar 2026 — busy guard per mutation-buttons (toggle/archive/attach).
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr(null);
    try { setRows(await apiGet("/api/admin/announcements")); }
    catch (e) { setErr(e?.data?.detail || e?.message || String(e)); setRows([]); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleActive = async (a) => {
    if (busy) return;
    setBusy(true);
    try {
      await apiPost(`/api/admin/announcements/${a.id}/toggle-active`, {});
      await load();
    } finally { setBusy(false); }
  };

  const archive = async (a) => {
    if (busy) return;
    if (!window.confirm(`Archiviare la comunicazione "${a.title}"?`)) return;
    setBusy(true);
    try {
      await apiPost(`/api/admin/announcements/${a.id}/archive`, {});
      await load();
    } finally { setBusy(false); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const uploadAttachment = async (a, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${backend}/api/admin/announcements/${a.id}/attachment`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token || ""}` },
      body: fd,
    });
    if (!res.ok) {
      const t = await res.text();
      alert("Upload fallito: " + t);
      return;
    }
    await load();
  };

  const removeAttachment = async (a) => {
    if (!window.confirm("Rimuovere l'allegato?")) return;
    await apiDelete(`/api/admin/announcements/${a.id}/attachment`);
    await load();
  };

  return (
    <Section title="Comunicazioni ai clienti"
             subtitle="Avvisi aziendali, chiusure, comunicazioni logistiche/commerciali"
             actions={
               <div style={{ display: "flex", gap: 8 }}>
                 <Link to="/admin"><Button size="sm">← Home Admin</Button></Link>
                 <Button size="sm" variant="primary"
                         onClick={() => setModal({ mode: "new" })}
                         data-testid="admin-ann-new">
                   <Plus size={14} /> Nuova
                 </Button>
               </div>
             }
             data-testid="admin-announcements-page">
      {err && <Alert tone="danger">Errore: {String(err)}</Alert>}
      {!rows ? (
        <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>
      ) : rows.length === 0 ? (
        <Card><Card.Body className="muted">Nessuna comunicazione creata.</Card.Body></Card>
      ) : (
        <Card>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  <th style={{ padding: 8 }}>Priorità</th>
                  <th style={{ padding: 8 }}>Titolo</th>
                  <th style={{ padding: 8 }}>Periodo</th>
                  <th style={{ padding: 8 }}>Target</th>
                  <th style={{ padding: 8 }}>Attivo</th>
                  <th style={{ padding: 8 }}>Extra</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-testid={`admin-ann-row-${a.id}`}
                      style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: 8 }}>
                      <Badge tone={PRIORITY_TONE[a.priority] || "neutral"}>
                        {PRIORITIES.find((p) => p.value === a.priority)?.label || a.priority}
                      </Badge>
                    </td>
                    <td style={{ padding: 8 }}><strong>{a.title}</strong></td>
                    <td style={{ padding: 8, fontSize: ".85rem" }}>
                      {fmtDate(a.starts_at)} → {fmtDate(a.ends_at)}
                    </td>
                    <td style={{ padding: 8, fontSize: ".85rem" }}>
                      {TARGET_TYPES.find((t) => t.value === a.target_type)?.label}
                      {a.target_region && ` · ${a.target_region}`}
                      {a.target_type === "SELECTED" && a.target_customer_ids &&
                        ` · ${a.target_customer_ids.length} clienti`}
                    </td>
                    <td style={{ padding: 8 }}>
                      <Badge tone={a.is_active ? "success" : "neutral"}>
                        {a.is_active ? "SÌ" : "NO"}
                      </Badge>
                    </td>
                    <td style={{ padding: 8, fontSize: ".85rem" }}>
                      {a.link_url && <Badge tone="neutral">link</Badge>}{" "}
                      {a.has_attachment && (
                        <Badge tone="neutral">
                          <Paperclip size={12} /> {a.attachment_filename?.slice(0, 20)}
                        </Badge>
                      )}
                    </td>
                    <td style={{ padding: 8, textAlign: "right", whiteSpace: "nowrap" }}>
                      <Button size="sm" onClick={() => setModal({ mode: "edit", row: a })}
                              data-testid={`admin-ann-edit-${a.id}`}>Modifica</Button>{" "}
                      <Button size="sm" onClick={() => toggleActive(a)}
                              loading={busy} disabled={busy}
                              data-testid={`admin-ann-toggle-${a.id}`}>
                        {a.is_active ? "Disattiva" : "Attiva"}
                      </Button>{" "}
                      <Button size="sm" onClick={() => setAttachTarget(a)}
                              data-testid={`admin-ann-attach-${a.id}`}>
                        <Paperclip size={12} /> Allegato
                      </Button>{" "}
                      <Button size="sm" onClick={() => archive(a)}
                              loading={busy} disabled={busy}
                              data-testid={`admin-ann-archive-${a.id}`}>Archivia</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {modal && (
        <AnnouncementModal
          mode={modal.mode}
          row={modal.row}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); }}
          apiPost={apiPost}
          apiPatch={apiPatch}
          apiGet={apiGet}
        />
      )}

      {attachTarget && (
        <AttachmentModal
          row={attachTarget}
          onClose={() => setAttachTarget(null)}
          onDone={async () => { setAttachTarget(null); await load(); }}
          upload={uploadAttachment}
          remove={removeAttachment}
        />
      )}
    </Section>
  );
}

function AnnouncementModal({ mode, row, onClose, onSaved, apiPost, apiPatch, apiGet }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    title: row?.title || "",
    body: row?.body || "",
    priority: row?.priority || "NORMAL",
    starts_at: row?.starts_at ? row.starts_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
    ends_at: row?.ends_at ? row.ends_at.slice(0, 10) : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    target_type: row?.target_type || "ALL",
    target_customer_category_id: row?.target_customer_category_id || "",
    target_region: row?.target_region || "",
    target_customer_ids: row?.target_customer_ids || [],
    link_url: row?.link_url || "",
    is_active: row?.is_active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    apiGet("/api/customers").then(setCustomers).catch(() => {});
    apiGet("/api/customer-categories").then(setCategories).catch(() => setCategories([]));
  }, []); // eslint-disable-line

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        title: form.title,
        body: form.body,
        priority: form.priority,
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        target_type: form.target_type,
        target_customer_category_id: form.target_type === "CATEGORY" ? form.target_customer_category_id || null : null,
        target_region: form.target_type === "REGION" ? form.target_region || null : null,
        target_customer_ids: form.target_type === "SELECTED" ? form.target_customer_ids : null,
        link_url: form.link_url || null,
        is_active: form.is_active,
      };
      if (isEdit) {
        await apiPatch(`/api/admin/announcements/${row.id}`, payload);
      } else {
        await apiPost("/api/admin/announcements", payload);
      }
      await onSaved();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
      setSaving(false);
    }
  };

  return (
    <div role="dialog" data-testid="admin-ann-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#fff", borderRadius: 8, width: "95%", maxWidth: 640,
                    maxHeight: "90vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>{isEdit ? "Modifica comunicazione" : "Nuova comunicazione"}</h3>
          <Button size="sm" onClick={onClose}><X size={14} /></Button>
        </div>
        {err && <Alert tone="danger">Errore: {String(err)}</Alert>}
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <div>
            <Label>Titolo</Label>
            <Input data-testid="admin-ann-form-title"
                   value={form.title} onChange={(e) => upd("title", e.target.value)} />
          </div>
          <div>
            <Label>Testo</Label>
            <textarea data-testid="admin-ann-form-body"
                      value={form.body} onChange={(e) => upd("body", e.target.value)}
                      rows={6}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1",
                               borderRadius: 6, fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label>Priorità</Label>
              <select data-testid="admin-ann-form-priority"
                      value={form.priority} onChange={(e) => upd("priority", e.target.value)}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Attiva</Label>
              <select value={form.is_active ? "1" : "0"}
                      onChange={(e) => upd("is_active", e.target.value === "1")}
                      data-testid="admin-ann-form-active"
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                <option value="1">Sì</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label>Inizio pubblicazione</Label>
              <Input type="date" value={form.starts_at}
                     data-testid="admin-ann-form-starts"
                     onChange={(e) => upd("starts_at", e.target.value)} />
            </div>
            <div>
              <Label>Fine pubblicazione</Label>
              <Input type="date" value={form.ends_at}
                     data-testid="admin-ann-form-ends"
                     onChange={(e) => upd("ends_at", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Destinatari</Label>
            <select value={form.target_type} onChange={(e) => upd("target_type", e.target.value)}
                    data-testid="admin-ann-form-target"
                    style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
              {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.target_type === "CATEGORY" && (
            <div>
              <Label>Categoria clienti</Label>
              <select value={form.target_customer_category_id}
                      onChange={(e) => upd("target_customer_category_id", e.target.value)}
                      data-testid="admin-ann-form-category"
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                <option value="">— seleziona —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name || c.code}</option>)}
              </select>
            </div>
          )}
          {form.target_type === "REGION" && (
            <div>
              <Label>Regione</Label>
              <Input value={form.target_region}
                     data-testid="admin-ann-form-region"
                     onChange={(e) => upd("target_region", e.target.value)}
                     placeholder="Es: Lombardia, Lazio, ..." />
            </div>
          )}
          {form.target_type === "SELECTED" && (
            <div>
              <Label>Clienti selezionati</Label>
              <div style={{ border: "1px solid #cbd5e1", borderRadius: 6, padding: 8,
                            maxHeight: 180, overflow: "auto" }}>
                {customers.length === 0 ? <p className="muted">Nessun cliente</p> :
                  customers.map((c) => (
                    <label key={c.id} style={{ display: "block", padding: 4 }}>
                      <input type="checkbox"
                             checked={form.target_customer_ids.includes(c.id)}
                             data-testid={`admin-ann-form-cust-${c.code || c.id}`}
                             onChange={(e) => {
                               const s = new Set(form.target_customer_ids);
                               if (e.target.checked) s.add(c.id); else s.delete(c.id);
                               upd("target_customer_ids", [...s]);
                             }} />
                      {" "}{c.code || ""} — {c.business_name}
                    </label>
                  ))
                }
              </div>
            </div>
          )}
          <div>
            <Label>Link esterno (opzionale)</Label>
            <Input value={form.link_url} onChange={(e) => upd("link_url", e.target.value)}
                   data-testid="admin-ann-form-link"
                   placeholder="https://..." />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={saving}
                  data-testid="admin-ann-form-save">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AttachmentModal({ row, onClose, onDone, upload, remove }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  return (
    <div role="dialog" data-testid="admin-ann-attach-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#fff", borderRadius: 8, width: "95%", maxWidth: 460, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Allegato: {row.title}</h3>
          <Button size="sm" onClick={onClose}><X size={14} /></Button>
        </div>
        <div style={{ marginTop: 12 }}>
          {row.has_attachment ? (
            <>
              <p><strong>File attuale:</strong> {row.attachment_filename}</p>
              <Button variant="danger" size="sm"
                      onClick={async () => { setBusy(true); try { await remove(row); onDone(); } finally { setBusy(false); } }}
                      loading={busy} disabled={busy}
                      data-testid="admin-ann-attach-remove">
                Rimuovi allegato
              </Button>
            </>
          ) : <p className="muted">Nessun allegato caricato.</p>}
          <hr style={{ margin: "12px 0" }} />
          <Label>Carica nuovo (PDF o DOC, max 20MB)</Label>
          <input type="file" accept=".pdf,.doc,.docx,application/pdf"
                 data-testid="admin-ann-attach-file"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, gap: 8 }}>
            <Button onClick={onClose}>Chiudi</Button>
            <Button variant="primary" disabled={!file}
                    data-testid="admin-ann-attach-upload"
                    onClick={async () => { if (file) { await upload(row, file); onDone(); } }}>
              <Upload size={14} /> Carica
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
